"use client";

import { c as _c } from "react/compiler-runtime";
import { CheckCircle2, Gavel, RotateCw, Trash2, TriangleAlert } from "lucide-react";
import FitHex from "@/features/collections/detail/FitHex";
import Pager from "@/features/collections/detail/Pager";
import { RowsSkeleton } from "@/features/collections/detail/parts";
import { DeskEmpty } from "@/features/home-v2/desk/Desk";
import { avatarOf } from "@/features/home-v2/tiles";
import { Avatar } from "@/features/home-v2/ui";
import { formatTokenSymbol } from "@/lib/asset-naming";
import { formatTokenPrice } from "@/lib/helpers";
import type { NftFinance } from "./useNftFinance";
type Tone = "wait" | "info" | "up" | "down" | "quiet";

/** Every status the offers API sends, in plain words; an unknown one is shown as sent, neutral. */
const STATUS: Record<string, [string, string, Tone]> = {
  pending: ["در انتظار ثبت", "Being recorded", "wait"],
  pending_wrap: ["در انتظار تبدیل", "Awaiting conversion", "wait"],
  pending_approve: ["در انتظار اجازه", "Awaiting permission", "wait"],
  pending_signature: ["در انتظار امضا", "Awaiting signature", "wait"],
  submitted: ["ثبت شده", "Submitted", "info"],
  success: ["فعال", "Active", "up"],
  fail: ["ناموفق", "Failed", "down"],
  pending_cancel: ["در حال لغو", "Cancelling", "wait"],
  cancelled: ["لغو شده", "Cancelled", "quiet"],
  pending_accept_approve: ["در انتظار تایید مالک", "Awaiting owner approval", "wait"],
  pending_accept: ["در حال پذیرش", "Accepting", "wait"],
  accepted: ["پذیرفته شد", "Accepted", "info"],
  obsolete: ["بی‌اعتبار", "Superseded", "quiet"],
  expired: ["منقضی", "Expired", "down"]
};
export function OfferSeal(t0) {
  const $ = _c(3);
  const {
    status,
    isFa
  } = t0;
  const s = STATUS[status];
  const t1 = s?.[2] ?? "quiet";
  const t2 = s ? isFa ? s[0] : s[1] : status;
  let t3;
  if ($[0] !== t1 || $[1] !== t2) {
    t3 = <span className="ns-seal" data-tone={t1}>{t2}</span>;
    $[0] = t1;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  return t3;
}
type OfferRow = NftFinance["sortedOffers"][number] & {
  tx_hash?: string | null;
  price_eth?: string | null;
  price_pol?: string | null;
  marketplace?: string | null;
};

/** Who made it: an account by its name, otherwise the wallet it came from (`user` is `[]` on a record from another market). */
function offererOf(offer: OfferRow) {
  const u = offer.user && !Array.isArray(offer.user) ? offer.user : null;
  return {
    name: u?.name || u?.username || null,
    address: offer.offerer_address || null
  };
}

/** The token it is priced in: the offer's own currency, else the chain coin the record names, else the page's. */
function symbolOf(offer: OfferRow, fallback: string) {
  const own = formatTokenSymbol(offer.currency?.symbol);
  if (own) return own;
  if (offer.price_pol != null && offer.price_pol !== "") return "POL";
  if (offer.price_eth != null && offer.price_eth !== "") return "ETH";
  return fallback;
}

/** The legacy board's date: medium date, short time, in the page's language. */
function when(value: string | undefined, isFa: boolean) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return new Intl.DateTimeFormat(isFa ? "fa-IR" : "en-US", {
    dateStyle: "medium",
    timeStyle: "short"
  }).format(date);
}

/**
 * THE OFFERS ON THIS TOKEN — the legacy board, every rule kept: highest
 * first, ten a page, the response's own total, the viewer's own offer
 * marked, accept only where `canAcceptOffer` allows and cancel only where
 * `canCancelOffer` does (each behind the login gate), and the four outcomes
 * apart — loading, failed (with a retry), empty, the list.
 */
export function NftOffers(t0) {
  const $ = _c(19);
  const {
    f
  } = t0;
  const {
    isFa,
    isOwner,
    isNftActive
  } = f;
  let t1;
  if ($[0] !== isFa) {
    t1 = (fa, en) => isFa ? fa : en;
    $[0] = isFa;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t = t1;
  const locale = isFa ? "fa-IR" : "en-US";
  let t2;
  if ($[2] !== f || $[3] !== isFa || $[4] !== isNftActive || $[5] !== isOwner || $[6] !== locale || $[7] !== t) {
    t2 = f.isOffersLoading ? <RowsSkeleton label={t("\u062F\u0631 \u062D\u0627\u0644 \u0628\u0627\u0631\u06AF\u0630\u0627\u0631\u06CC \u0622\u0641\u0631\u0647\u0627", "Loading offers")} rows={3} /> : f.isOffersError ? <DeskEmpty tone="error" icon={<TriangleAlert />} title={t("\u0622\u0641\u0631\u0647\u0627 \u0628\u0627\u0631\u06AF\u0630\u0627\u0631\u06CC \u0646\u0634\u062F", "The offers could not be loaded")} body={t("\u0628\u0642\u06CC\u0647\u200C\u06CC \u0627\u06CC\u0646 \u0635\u0641\u062D\u0647 \u0633\u0627\u0644\u0645 \u0627\u0633\u062A.", "The rest of this page is unaffected.")} actions={<button type="button" className="v2-btn v2-btn--ghost v2-focus" onClick={() => void f.refetchOffers()}><RotateCw aria-hidden={true} />{t("\u062A\u0644\u0627\u0634 \u062F\u0648\u0628\u0627\u0631\u0647", "Try again")}</button>} /> : f.sortedOffers.length === 0 ? <DeskEmpty icon={<Gavel />} title={t("\u0647\u0646\u0648\u0632 \u0622\u0641\u0631\u06CC \u062B\u0628\u062A \u0646\u0634\u062F\u0647", "No offers yet")} body={isOwner ? t("\u0622\u0641\u0631\u0647\u0627\u06CC\u06CC \u06A9\u0647 \u0628\u0631\u0627\u06CC \u0627\u06CC\u0646 \u0627\u062B\u0631 \u0645\u06CC\u200C\u06AF\u06CC\u0631\u06CC\u062F \u0627\u06CC\u0646\u062C\u0627 \u0645\u06CC\u200C\u0622\u06CC\u0646\u062F \u0648 \u0645\u06CC\u200C\u062A\u0648\u0627\u0646\u06CC\u062F \u0628\u067E\u0630\u06CC\u0631\u06CC\u062F\u0634\u0627\u0646.", "Offers you receive for this piece will appear here, ready to accept.") : !isNftActive ? t("\u0627\u06CC\u0646 NFT \u0641\u0639\u0627\u0644 \u0646\u06CC\u0633\u062A\u060C \u0628\u0646\u0627\u0628\u0631\u0627\u06CC\u0646 \u0622\u0641\u0631\u06CC \u0631\u0648\u06CC \u0622\u0646 \u062B\u0628\u062A \u0646\u0645\u06CC\u200C\u0634\u0648\u062F.", "This NFT is not active, so it can't receive offers.") : t("\u0627\u0648\u0644\u06CC\u0646 \u0646\u0641\u0631\u06CC \u0628\u0627\u0634\u06CC\u062F \u06A9\u0647 \u0628\u0631\u0627\u06CC \u0627\u06CC\u0646 \u0627\u062B\u0631 \u0642\u06CC\u0645\u062A \u067E\u06CC\u0634\u0646\u0647\u0627\u062F \u0645\u06CC\u200C\u062F\u0647\u062F\u061B \u0645\u0627\u0644\u06A9 \u0645\u06CC\u200C\u062A\u0648\u0627\u0646\u062F \u0622\u0646 \u0631\u0627 \u0628\u067E\u0630\u06CC\u0631\u062F.", "Be the first to make an offer on this piece \u2014 the owner can accept it.")} actions={!isOwner && isNftActive ? <button type="button" className="v2-btn v2-focus" onClick={f.openOffer}><Gavel aria-hidden={true} />{t("\u062B\u0628\u062A \u0622\u0641\u0631", "Place an offer")}</button> : undefined} /> : <ul className="ns-offers" data-busy={f.isOffersFetching || undefined}>{(f.sortedOffers as OfferRow[]).map((offer, i) => {
        const r = f.offerRights(offer);
        const {
          name,
          address
        } = offererOf(offer);
        const who = name || address || t("\u06A9\u0627\u0631\u0628\u0631", "User");
        const key = Number(offer.id) > 0 ? `o-${offer.id}` : offer.tx_hash ? `tx-${offer.tx_hash}` : `i-${i}`;
        return <li key={key} className="ns-offer" data-accepted={offer.status === "accepted" || undefined}><Avatar name={name || " "} src={avatarOf(offer.user)} size={36} /><span className="ns-offer-who"><b>{name ? <bdi>{name}</bdi> : address ? <FitHex value={address} /> : <bdi>{who}</bdi>}{r.mine ? <i>{t("\u0622\u0641\u0631 \u0634\u0645\u0627", "You")}</i> : null}</b><small>{when(offer.created_at, isFa)}</small></span><span className="ns-offer-end"><span className="ns-offer-price"><bdi>{formatTokenPrice(offer.total_price, locale)}</bdi><small>{symbolOf(offer, f.currencySymbol)}</small></span><OfferSeal status={offer.status} isFa={isFa} /></span>{r.canAccept || r.canCancel ? <span className="ns-offer-tools">{r.canAccept ? <button type="button" className="v2-btn v2-btn--ghost ns-accept v2-focus" onClick={() => f.acceptOffer(offer)}><CheckCircle2 aria-hidden={true} />{t("\u067E\u0630\u06CC\u0631\u0634", "Accept")}</button> : null}{r.canCancel ? <button type="button" className="v2-btn v2-btn--ghost as-danger-ghost v2-focus" onClick={() => f.cancelOffer(offer)}><Trash2 aria-hidden={true} />{t("\u0644\u063A\u0648", "Cancel")}</button> : null}</span> : null}</li>;
      })}</ul>;
    $[2] = f;
    $[3] = isFa;
    $[4] = isNftActive;
    $[5] = isOwner;
    $[6] = locale;
    $[7] = t;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const body = t2;
  let t3;
  if ($[9] !== f.isOffersError || $[10] !== f.isOffersFetching || $[11] !== f.isOffersLoading || $[12] !== f.offersPageMeta || $[13] !== f.setOffersPage || $[14] !== isFa) {
    t3 = !f.isOffersLoading && !f.isOffersError && f.offersPageMeta && f.offersPageMeta.last_page > 1 ? <Pager page={f.offersPageMeta.current_page} last={f.offersPageMeta.last_page} busy={f.isOffersFetching} onPage={f.setOffersPage} isFa={isFa} /> : null;
    $[9] = f.isOffersError;
    $[10] = f.isOffersFetching;
    $[11] = f.isOffersLoading;
    $[12] = f.offersPageMeta;
    $[13] = f.setOffersPage;
    $[14] = isFa;
    $[15] = t3;
  } else {
    t3 = $[15];
  }
  const pager = t3;
  let t4;
  if ($[16] !== body || $[17] !== pager) {
    t4 = <div className="ns-offers-wrap">{body}{pager}</div>;
    $[16] = body;
    $[17] = pager;
    $[18] = t4;
  } else {
    t4 = $[18];
  }
  return t4;
}